import React, { useRef, useCallback } from 'react';
import Card, {
	CardBody,
	CardFooter,
	CardFooterRight,
	CardHeader,
	CardLabel,
	CardTitle,
} from './bootstrap/Card';
import Label from './bootstrap/forms/Label';
import Button from './bootstrap/Button';
import Input from './bootstrap/forms/Input';
import classNames from 'classnames';
import '../pages/presentation/demo-pages/customvset.css';
import Icon from '../components/icon/Icon';

const ImageUploader = ({
	label,
	selectedImages = [],
	onImageSelect,
	onImageDelete,
	accept = 'image/*',
	multiple = false,
	maxImages = 1,
	minDimensions = { width: 160, height: 40 },
	showAddButton = true,
	progress = [],
	typeError = false,
	darkModeStatus = false,
	themeStatus = false,
	inputRef,
	t = (text) => text,
}) => {
	const fileInputRef = useRef(inputRef || null);

	const handleAddButtonClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
			fileInputRef.current.click();
		}
	};

	const AddImageButton = useCallback(() => {
		if (!showAddButton || selectedImages.length >= maxImages) return null;

		const borderColor = darkModeStatus ? '#666' : '#ccc';

		return (
			<div className='mb-3'>
				<div
					style={{
						width: '128px',
						height: '128px',
						border: `2px dashed ${borderColor}`,
						borderRadius: '10px',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
						transition: 'all 0.3s ease',
						color: borderColor,
					}}
					className='add-image-button'
					onClick={handleAddButtonClick}>
					<Icon icon='PlusCircle' size='2x' className='mb-2' color={borderColor} />
					<span
						style={{
							fontSize: '12px',
							textAlign: 'center',
							color: borderColor,
							lineHeight: '1.2',
						}}>
						{t('Click to add')}
						<br />
						{t('Photo')}
					</span>
				</div>
			</div>
		);
	}, [showAddButton, selectedImages.length, maxImages, t, darkModeStatus]);

	const renderImageWithProgress = (element, index) => {
		const isFirstLine = index < 2;
		const progressData = progress.find((p) => p.id === element.id);

		return (
			<div
				key={element.id}
				className={'mb-3'}
				style={{
					width: '128px',
					marginLeft: '0',
				}}>
				<div
					style={{
						borderRadius: '10px',
						overflow: 'hidden',
						position: 'relative',
						width: '128px',
						height: '128px',
					}}>
					<img
						src={element.file}
						alt=''
						width={128}
						height={128}
						className='img-fluid'
						style={{
							objectFit: 'contain',
							height: 'inherit',
							backgroundColor: '#4d69fa22',
							border: '1px solid #4d69fa55',
							borderRadius: '8px',
						}}
					/>
					<div className='dev-b'>
						<Button
							isOutline={!darkModeStatus}
							color='dark'
							isLight={darkModeStatus}
							className={classNames('text-nowrap', {
								'border-light': !darkModeStatus,
							})}
							icon='Delete'
							onClick={() => onImageDelete(index)}
						/>
					</div>
				</div>
				{progressData?.uploading && (
					<div
						className='progress'
						style={{
							height: '5px',
							marginTop: '5px',
						}}>
						<div
							className='progress-bar progress-bar-striped progress-bar-animated'
							role='progressbar'
							style={{
								width: `${progressData.progress}%`,
							}}
						/>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className='col-12' style={{ width: 'fit-content' }}>
			<Label>
				{t(label)}
				<br />
				<p style={{ fontSize: '10px' }}>
					(Min: {minDimensions.width}px x {minDimensions.height}px)
				</p>
			</Label>
			<div className='col-12' style={{ width: 'fit-content' }}>
				{Array.isArray(selectedImages) && selectedImages.length > 0 ? (
					<div className='col-12 d-flex'>
						{selectedImages.map(renderImageWithProgress)}
					</div>
				) : (
					<div className='col-12 d-flex' style={{}}>
						<AddImageButton />
					</div>
				)}
				{typeError.error && (
					<p
						style={{
							color: 'red',
							fontSize: '12px',
							width: '100%',
							textAlign: 'center',
						}}>
						{typeError.msg ? typeError.msg : t('*Required')}
					</p>
				)}
			</div>

			<div className='col-12'>
				<div className='row g-4'>
					<div className='col-12'>
						<Input
							ref={fileInputRef}
							type='file'
							id='filepath'
							autoComplete='photo'
							accept={accept}
							multiple={multiple}
							style={{ display: 'none' }}
							onChange={onImageSelect}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ImageUploader;
